import { action, makeObservable, observable } from "mobx";
import {
	fetchProtectedResource,
	fetchResource,
	isNumeric,
} from "../../utilities";
import { message } from "antd";
import { isArray } from "lodash";
import BaseService from "../base/BaseService";
import {
	QUOTE_CANCEL_URL,
	QUOTE_QUOTES_URL,
	QUOTE_CALCULATE_CANCEL_URL,
} from "../../configs/api-paths";

/**
 * Service dealing with policies reporting.
 */
class QuotesService extends BaseService {
	/**
	 * All policies.
	 * @type {[]}
	 */
	@observable
	quotesData = [];

	/**
	 * Is the service loading data.
	 * @type {boolean}
	 */
	@observable
	loading = false;

	/**
	 * Policies table pagination object.
	 * @type {{}}
	 */
	@observable
	pagination = {};

	/**
	 * Policies table sorting object.
	 * @type {{}}
	 */
	@observable
	sorter = {
		field: "renewalDate",
		order: "descend",
	};

	@observable
	search = {};

	@observable
	cancellationModalVisible = false;

	@observable
	cancellationModalRecord;

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setquotesData = (data) => {
		this.quotesData = data;
	};

	@action
	setLoading = (loading) => {
		this.loading = loading;
	};

	@action
	setPagination = (pagination) => {
		this.pagination = pagination;
	};

	@action
	setSorter = (sorter) => {
		this.sorter = sorter;
	};

	@action
	setSearch = (search) => {
		if (search.value !== this.search.value) {
			this.pagination = {
				...this.pagination,
				current: 1,
			};
		}
		this.search = search;
	};

	@action
	showCancellationModal = () => {
		this.cancellationModalVisible = true;
	};

	@action
	hideCancellationModal = () => {
		this.cancellationModalVisible = false;
	};

	@action
	setCancellationModalRecord = (record) => {
		this.cancellationModalRecord = record;
	};

	/**
	 * @returns {Promise<void>}
	 */
	loadQuotesData = async () => {
		this.setLoading(true);
		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			let field = this.sorter.field;
			const order = this.sorter.order;

			const currentPage = this.pagination.current;
			const pageSize = this.pagination.pageSize;

			let url = `${QUOTE_QUOTES_URL}?`;

			if (field && order) {
				if (field === "first_name") {
					field = "organisation.firstName";
				}

				if (field === "last_name") {
					field = "organisation.lastName";
				}

				if (field === "phone") {
					field = "organisation.phone";
				}

				if (field === "address1") {
					field = "organisation.address1";
				}

				if (field === "address2") {
					field = "organisation.address2";
				}

				if (field === "city") {
					field = "organisation.city";
				}

				if (field === "post_code") {
					field = "organisation.postcode";
				}

				if (field === "quoteId") {
					field = "id";
				}

				url += `sort=id,${order === "ascend" ? "asc" : "desc"}&`; // sort by Quote ID descening
			}

			if (isNumeric(currentPage) && isNumeric(pageSize)) {
				url += `page=${currentPage - 1}&size=${pageSize}&`;
			}

			if (isArray(this.search.fields)) {
				const _searchFields = this.search.fields.map((field) => {
					let _field = field;
					if (field === "organisation.first_name") {
						_field = "organisation.firstName";
					}
					if (field === "organisation.last_name") {
						_field = "organisation.lastName";
					}
					if (field === "organisation.post_code") {
						_field = "organisation.postcode";
					}
					return _field;
				});
				// url += `searchFields=${this.search.fields.join(",")}&`;
				url += `searchFields=${_searchFields.join(",")}&`;
				url += `searchValue=${this.search.value}&`;
			}

			const result = await fetchProtectedResource(url);
			hideLoadingMessage();
			const pagedResults = await result.json();

			this.setPagination({
				...this.pagination,
				total: pagedResults.totalElements,
			});
			this.setquotesData(pagedResults.content);
		} catch (error) {
			console.log(error);

			hideLoadingMessage();
			message.error(
				`There was error while trying to fetch policies data from the server. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};
}

export default QuotesService;
